import {
  ACTIVE_MATCH_MODE,
  AI,
  BODY_CHECK,
  FIELD,
  FIRST_TIME,
  GK,
  NUTMEG,
  goalMouth,
  POSSESSION,
  TACTICS,
  fieldPlayersPerTeam,
  type Ball,
  type GameAction,
  type InputFrame,
  type Player,
  type Possession,
  type TeamSide,
  type Vec2,
} from "@golazo/shared";
import { clamp, vec, len2 } from "../core/math.js";
import { predictGoalLineCrossing } from "../physics/ballPrediction.js";
import { signed, type Random } from "../core/random.js";
import { findNutmegTarget, isLateralCut } from "../game/kick.js";
import { chooseAttack, evaluateShot, laneClearance, scatterDir } from "./attack.js";
import { findFirstTimeWindow } from "../game/firstTime.js";
import { attackMentality, planOffBall, type TeamTacticsState, type TacticsWorld } from "./tactics.js";
import { bodyScale, firstTimeScale, keeperReactionScale, reactionScale } from "./skill.js";

export interface AiWorld {
  players: readonly Player[];
  ball: Ball;
  possession: Possession;
  tactics: TacticsWorld;
  /**
   * Jugador que lleva el humano. La IA nunca le asigna tareas: no puede contar
   * con que vaya a presionar ni a perseguir el balon. Si se le delega y el
   * humano no va, no va nadie, y el partido se queda bloqueado.
   */
  humanId: string | null;
}

export interface AiController {
  readonly playerId: string;
  update(world: AiWorld, dt: number): InputFrame;
}

/** El mismo `InputFrame` que consume el partido, pero escribible desde aqui. */
interface AiFrame extends InputFrame {
  held: Set<GameAction>;
  pressed: Set<GameAction>;
  kickPower: number;
  kickKind?: "pass" | "shoot" | "cross";
}

/**
 * Fija el destino. `arrive` es la zona muerta de llegada y `exact` desactiva el
 * sesgo personal de colocacion (se usa al ir al balon, que hay que pisarlo).
 */
type SetDesired = (x: number, y: number, arrive?: number, exact?: boolean) => void;

interface GkReaction {
  diveY: number | null;
  reactionLeft: number;
}

const HALF_HEIGHT = FIELD.HEIGHT / 2;

/** Porteria que defiende: `home` la izquierda, `away` la derecha. */
function defendedGoal(side: TeamSide): Vec2 {
  return vec(side === "home" ? 0 : FIELD.WIDTH, HALF_HEIGHT);
}

function attackDir(side: TeamSide): number {
  return side === "home" ? 1 : -1;
}

function dist(a: Vec2, b: Vec2): number {
  return len2(a.x - b.x, a.y - b.y);
}

function nearestTo(target: Vec2, candidates: readonly Player[]): Player | null {
  let best: Player | null = null;
  let bestDist = Infinity;
  for (const p of candidates) {
    const d = dist(p.pos, target);
    if (d < bestDist) {
      best = p;
      bestDist = d;
    }
  }
  return best;
}

export function createAiController(player: Player, random: Random): AiController {
  const move: Vec2 = vec();
  const held = new Set<GameAction>();
  const pressed = new Set<GameAction>();
  const frame: AiFrame = { move, held, pressed, kickPower: 1 };

  // Punto al que va ahora mismo. Solo se recalcula cada DECISION_INTERVAL,
  // lo que da un tiempo de reaccion humano y evita que tiemble entre dos ideas.
  const desired: Vec2 = vec(player.spawn.x, player.spawn.y);
  // Carrera: la IA del barrio piensa mas despacio (ver `skill.ts`). Sin nivel, x1.
  const reaction = reactionScale(player);
  let decisionTimer = random() * ACTIVE_MATCH_MODE.aiDecisionInterval * reaction;
  /** Segundos que lleva con el balon controlado. Hay que asentarlo antes de rematar. */
  let holdTime = 0;

  // Sesgo fijo por jugador: dos companeros con el mismo rol no se pisan.
  const noiseX = signed(random) * AI.POSITION_NOISE;
  const noiseY = signed(random) * AI.POSITION_NOISE;

  // Caracter fijo del jugador: cuanto se atreve por encima o por debajo de lo
  // que pide su rol. Se sortea una sola vez al crear el cerebro (no por tick),
  // asi que es un rasgo estable y reproducible con la semilla, no ruido.
  const boldnessTrait = 1 + signed(random) * AI.BOLDNESS_VARIATION;

  /**
   * Error personal de timing en el golpeo de primera (segundos).
   *
   * La IA VE la ventana exacta, asi que sin esto clavaria el contacto perfecto
   * siempre y tendria un gesto que ningun humano puede igualar. Se sortea una
   * vez por cerebro, igual que la audacia: unos jugadores le pegan mejor de
   * primera que otros, y es estable y reproducible con la semilla.
   */
  const firstTimeError = firstTimeScale(player);
  const firstTimeBias = signed(random) * AI.FIRST_TIME_BIAS * firstTimeError;
  /** Enfriamiento: sin el, un rechace se remata en bucle tick tras tick. */
  let firstTimeCooldown = 0;
  /** Instante objetivo de ESTE intento. Se sortea al abrirse la ventana. */
  let firstTimeTarget: number | null = null;

  // Zona muerta de llegada. Al ir a por el balon se pone a cero: hay que
  // pisarlo, no quedarse cerca. Con la zona muerta general nunca lo alcanzaban.
  let arriveRadius: number = AI.ARRIVE_RADIUS;

  // Estado de reaccion del portero ante disparos.
  let gkDiveY: number | null = null;
  let gkReactionLeft = 0;
  let attackTarget: Vec2 | null = null;

  const setDesired: SetDesired = (x, y, arrive = AI.ARRIVE_RADIUS, exact = false): void => {
    arriveRadius = arrive;
    // Al ir a un punto exacto (el balon) no se aplica el sesgo personal.
    const bx = exact ? 0 : noiseX;
    const by = exact ? 0 : noiseY;
    desired.x = clamp(x + bx, player.radius, FIELD.WIDTH - player.radius);
    desired.y = clamp(y + by, player.radius, FIELD.HEIGHT - player.radius);
  };

  return {
    playerId: player.id,

    update(world: AiWorld, dt: number): InputFrame {
      pressed.clear();
      held.clear();
      frame.kickKind = undefined;
      frame.kickPower = 1;
      frame.shotStyle = undefined;

      const { players, ball, possession } = world;
      const mates = players.filter((p) => p.side === player.side && p !== player);
      const opponents = players.filter((p) => p.side !== player.side);
      const owner = possession.ownerId
        ? (players.find((p) => p.id === possession.ownerId) ?? null)
        : null;

      decisionTimer -= dt;
      const decide = decisionTimer <= 0;
      if (decide) decisionTimer = ACTIVE_MATCH_MODE.aiDecisionInterval * reaction;

      if (owner === player) {
        holdTime += dt;
        if (player.role === "gk") {
          // El portero con balon no evalua tiros a puerta rival ni regates:
          // decide entre saque corto, despeje largo o asentarse un instante.
          goalkeeperDistribution(
            player,
            ball,
            mates,
            opponents,
            frame,
            decide,
            holdTime,
            random,
            (t) => {
              attackTarget = t;
            },
            world.tactics[player.side].urgency,
          );
        } else {
          onBall(
            player,
            ball,
            mates,
            opponents,
            world.tactics[player.side],
            frame,
            decide,
            holdTime,
            random,
            (t) => {
              attackTarget = t;
            },
            boldnessTrait,
            world.possession.kickoff?.takerId === player.id,
          );
        }
        if (!decide && attackTarget) {
          steerTo(player, attackTarget, move);
        }
        return frame;
      }
      holdTime = 0;
      attackTarget = null;

      // Recibir un balon caliente exige Shift pulsado EN el instante del
      // toque (ver possession.ts::applyFirstTouch). La IA no tiene botones
      // que anticipar solo, asi que se prepara sola: si el balon viene hacia
      // ella con velocidad de pase/tiro, sostiene control cerrado como
      // brace, igual que un jugador real prepara el cuerpo para amortiguar.
      // Sin esto, la IA fallaria sistematicamente los primeros toques y el
      // humano tendria una ventaja que no viene de jugar mejor.
      const bracing = isBracingForBall(player, ball);

      // De PRIMERA: el balon llega y merece la pena no pararlo.
      firstTimeCooldown = Math.max(0, firstTimeCooldown - dt);
      const firstTime = findFirstTimeWindow(ball, player, possession);
      if (!firstTime) firstTimeTarget = null;
      if (firstTime && player.role !== "gk" && firstTimeCooldown <= 0) {
        // El instante objetivo se fija al abrirse la ventana y se mantiene
        // durante todo el intento.
        if (firstTimeTarget === null) {
          // Nunca negativo: si el instante elegido cae despues del contacto, la
          // IA COMETE el error y golpea tarde, como haria un humano. Dejarlo
          // negativo hacia que no disparase, y entonces solo ejecutaba los
          // intentos que le salian bien.
          firstTimeTarget = Math.max(
            0,
            FIRST_TIME.SWEET_SPOT + firstTimeBias + signed(random) * AI.FIRST_TIME_JITTER * firstTimeError,
          );
        }
        if (firstTime.timeToContact <= firstTimeTarget) {
          let nearest = Infinity;
          for (const rival of opponents) {
            const d = len2(rival.pos.x - player.pos.x, rival.pos.y - player.pos.y);
            if (d < nearest) nearest = d;
          }
          const shot = evaluateShot(player, ball, opponents, nearest);
          // Rematar solo si de verdad hay disparo; si no, se controla, que casi
          // siempre es mejor que un pelotazo de primera.
          if (shot.score > AI.FIRST_TIME_SHOOT_MIN) {
            firstTimeCooldown = AI.FIRST_TIME_COOLDOWN;
            aimAt(player, shot.target, move);
            pressed.add("shoot");
            return frame;
          }
        }
      }

      if (decide) {
        if (player.role === "gk") {
          const reaction: GkReaction = { diveY: gkDiveY, reactionLeft: gkReactionLeft };
          goalkeeping(player, ball, owner, players, world.humanId, random, setDesired, reaction);
          gkDiveY = reaction.diveY;
          gkReactionLeft = reaction.reactionLeft;
        } else {
          offBall(
            player,
            ball,
            owner,
            mates,
            opponents,
            world.tactics[player.side],
            setDesired,
          );
        }
      }

      if (player.role === "gk") {
        gkReactionLeft = Math.max(0, gkReactionLeft - dt);

        if (gkDiveY !== null && gkReactionLeft <= 0) {
          const goal = defendedGoal(player.side);
          setDesired(
            goal.x + (player.side === "home" ? 1 : -1) * (player.radius + 4),
            gkDiveY,
            0,
            true,
          );
        } else if (gkReactionLeft > 0) {
          const goal = defendedGoal(player.side);
          setDesired(
            goal.x + (player.side === "home" ? 1 : -1) * (player.radius + 4),
            player.pos.y,
            AI.ARRIVE_RADIUS,
            true,
          );
        }
      }

      steerTo(player, desired, move, arriveRadius);

      // Ayuda de recepcion: si el balon viene hacia este jugador, corrige el
      // rumbo tactico un poco hacia el, en vez de dejarlo pasar de largo
      // porque su objetivo de formacion apunta a otro sitio. Parcial a
      // proposito (RECEPTION_ASSIST_BLEND) — sigue siendo el mismo `steerTo`
      // el que manda la mayoria del movimiento.
      if (bracing) {
        const toBallX = ball.pos.x - player.pos.x;
        const toBallY = ball.pos.y - player.pos.y;
        const len = len2(toBallX, toBallY);
        if (len > 1e-6) {
          const blend = AI.RECEPTION_ASSIST_BLEND;
          move.x = move.x * (1 - blend) + (toBallX / len) * blend;
          move.y = move.y * (1 - blend) + (toBallY / len) * blend;
          const moveLen = len2(move.x, move.y);
          if (moveLen > 1) {
            move.x /= moveLen;
            move.y /= moveLen;
          }
        }
      }

      if (decide && owner && owner.side !== player.side && player.role !== "gk") {
        tryBodyCheck(player, owner, frame, random);
      }

      return frame;
    },
  };
}

/**
 * Embestir al que lleva el balon (la O sin balon, `game/bodyCheck.ts`).
 *
 * Solo con el rival a tiro y de frente o de lado: por la espalda seria un
 * empujon, y dejarlo fuera es lo que da valor a cubrirse de espaldas. El
 * `random()` se tira el ULTIMO, cuando todo lo demas ya se cumple: asi un
 * partido sin ocasiones de cuerpo consume la misma secuencia que antes.
 */
function tryBodyCheck(player: Player, owner: Player, frame: AiFrame, random: Random): void {
  const scale = bodyScale(player);
  if (scale <= 0) return;
  if (player.dashCooldown > 0 || player.stumbleTime > 0 || player.trapTime > 0) return;
  const dx = owner.pos.x - player.pos.x;
  const dy = owner.pos.y - player.pos.y;
  const d = len2(dx, dy);
  if (d > BODY_CHECK.REACH || d < 1e-3) return;
  const front = (-dx / d) * owner.facing.x + (-dy / d) * owner.facing.y;
  if (front < AI.BODY_CHECK_FRONT_COS) return;
  if (random() >= AI.BODY_CHECK_CHANCE * scale) return;
  frame.move.x = dx / d;
  frame.move.y = dy / d;
  frame.pressed.add("dash");
}

/**
 * ¿Va a tenerle a tiro un rival en `AI.BRACE_LOOKAHEAD` segundos? Es lo que ve
 * venir quien lleva el balon: alguien cerca que se le echa encima. No mira si
 * el rival tiene la O disponible, porque eso no se ve desde el campo.
 */
function bodyThreat(player: Player, opponents: readonly Player[]): boolean {
  for (const o of opponents) {
    if (o.role === "gk" || o.stumbleTime > 0) continue;
    const dx = player.pos.x - o.pos.x;
    const dy = player.pos.y - o.pos.y;
    const d = len2(dx, dy);
    if (d <= BODY_CHECK.REACH) return true;
    // Sin tope fijo de distancia: acercandose los dos, un rival a 150 esta a
    // tiro en menos de una decision, y con el tope no se le veia venir nunca.
    const closing = ((o.vel.x - player.vel.x) * dx + (o.vel.y - player.vel.y) * dy) / d;
    if (d - closing * AI.BRACE_LOOKAHEAD <= BODY_CHECK.REACH) return true;
  }
  return false;
}

/**
 * Con balon. Compara puntuacion de disparo vs pase vs conduccion y ejecuta
 * la mejor opcion. Ya no dispara en cuanto entra en rango.
 */
function onBall(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
  team: TeamTacticsState,
  frame: AiFrame,
  decide: boolean,
  holdTime: number,
  random: Random,
  setAttackTarget: (target: Vec2 | null) => void,
  /** Caracter fijo de ESTE jugador, sobre la base de audacia de su rol. */
  boldnessTrait: number,
  /** Este jugador esta sacando de centro y el balon aun no se ha jugado. */
  kickingOff: boolean,
): void {
  const { pressed, move } = frame;

  const pressure = nearestTo(player.pos, opponents);
  const pressureDist = pressure ? dist(pressure.pos, player.pos) : Infinity;
  /*
   * Aqui vivia la decision de "control cerrado": la IA frenaba para proteger
   * el balon cuando notaba presion. Ya no existe ese boton —el balon se pega
   * solo— asi que la cuenta entera se ha ido con el.
   */

  if (!decide) return;
  if (player.trapTime > 0) {
    // Primer toque exigente: orientarse al apoyo, no al gol. Prepara el un-toque.
    const support = mates
      .filter((m) => m.role !== "gk")
      .sort((a, b) => dist(a.pos, player.pos) - dist(b.pos, player.pos))[0];
    if (support && dist(support.pos, player.pos) < AI.SHORT_PASS_MAX * 1.1) {
      setAttackTarget(support.pos);
      steerTo(player, support.pos, move);
    } else {
      const goalX = player.side === "home" ? FIELD.WIDTH : 0;
      const standoff = AI.SHOOT_STANDOFF * ACTIVE_MATCH_MODE.ai.standoffScale;
      setAttackTarget(vec(goalX - attackDir(player.side) * standoff, player.pos.y));
      steerTo(player, { x: goalX - attackDir(player.side) * standoff, y: HALF_HEIGHT }, move);
    }
    return;
  }

  /*
   * Sacando de centro: el pase va ATRAS, y la IA lo sabe.
   *
   * `kickBall` proyecta hacia atras cualquier golpeo del que saca, asi que sin
   * esta rama la IA apuntaria al hueco de siempre —hacia delante— y el balon
   * saldria en horizontal hacia nadie. La regla no puede ser una trampa para
   * quien no la conoce.
   *
   * Se elige al companero MAS RETRASADO de los que estan por detras, no al mas
   * cercano: el mas cercano en un saque de centro suele ser el otro delantero,
   * que esta en la misma linea y recibe igual de vendido.
   */
  if (kickingOff) {
    // Se asienta el balon antes de jugarlo. Sacar en el primer tick se lee
    // como un robot; mirar arriba un instante, como un futbolista.
    if (holdTime < AI.KICKOFF_MIN_HOLD) {
      setAttackTarget(null);
      move.x = 0;
      move.y = 0;
      return;
    }
    const back = attackDir(player.side) * -1;
    const behind = mates
      .filter((m) => (m.pos.x - player.pos.x) * back > 0)
      .sort((a, b) => (b.pos.x - a.pos.x) * back);
    // El portero es el ultimo recurso: si hay alguien de campo detras, a el.
    const target = behind.find((m) => m.role !== "gk") ?? behind[0] ?? null;
    if (target) {
      setAttackTarget(null);
      aimAt(player, target.pos, move);
      frame.kickPower = AI.GK_SHORT_KICK_POWER;
      frame.kickKind = "pass";
      pressed.add("pass");
      return;
    }
  }

  const mentality = attackMentality(player, team);
  // Rol (en attackMentality) x caracter individual. Un central audaz sigue
  // siendo mas conservador que un delantero timido: el rol pesa mas.
  mentality.boldness *= boldnessTrait;
  mentality.shootBias *= ACTIVE_MATCH_MODE.ai.shootBias;
  // Tempo del equipo amplifica el sesgo de pase (tiki-taka).
  mentality.passBias *= ACTIVE_MATCH_MODE.ai.passBias * (0.85 + team.instructions.tempo * 0.4);
  mentality.dribbleBias *= 1.1 - team.instructions.tempo * 0.35;
  // Perdiendo al final se prueba desde donde antes se buscaba el pase. El
  // alcance del tiro no cambia (`shootRange`): se chuta antes, no desde mas
  // lejos de lo que el balon llega.
  if (team.urgency > 0) mentality.shootBias *= 1 + team.urgency * TACTICS.CHASE_SHOOT_GAIN;

  const choice = chooseAttack(
    player,
    ball,
    mates,
    opponents,
    holdTime,
    pressureDist,
    mentality,
    team,
    random(),
  );

  // Caño/corte: solo 3v3/2v2 (plantillas chicas). En 5v5+ el dash es escape,
  // no skill-move spam.
  const skillMoves = fieldPlayersPerTeam() <= 3;

  // Caño raro: rival en cono + O. Solo roles audaces / bajo presion cercana.
  const nutmegVictim = skillMoves ? findNutmegTarget(player, opponents, null) : null;
  if (
    nutmegVictim &&
    player.dashCooldown <= 0 &&
    player.skillCooldown <= 0 &&
    player.trapTime <= 0 &&
    holdTime > 0.14 &&
    pressureDist < NUTMEG.MAX_DIST * 1.1 &&
    mentality.boldness > 0.55 &&
    random() < AI.NUTMEG_CHANCE * mentality.boldness
  ) {
    setAttackTarget(null);
    aimAt(player, nutmegVictim.pos, move);
    pressed.add("dash");
    return;
  }

  if (choice.kind === "fake") {
    setAttackTarget(choice.target);
    steerTo(player, choice.target, move);
    // Corte seco: si el escape es lateral, fake+strafe en vez de finta recta.
    const escape = {
      x: choice.target.x - player.pos.x,
      y: choice.target.y - player.pos.y,
    };
    if (skillMoves && isLateralCut(player.facing, escape) && random() < AI.CUT_CHANCE) {
      pressed.add("fake");
      return;
    }
    pressed.add("fake");
    return;
  }

  if (choice.kind === "shoot") {
    setAttackTarget(null);
    aimAt(player, choice.target, move);
    scatterDir(move, choice.spread, random);
    frame.kickPower = choice.power;
    frame.kickKind = "shoot";
    frame.shotStyle = choice.shotStyle;
    pressed.add("shoot");
    return;
  }

  if (choice.kind === "cross") {
    setAttackTarget(null);
    aimAt(player, choice.target, move);
    scatterDir(move, choice.spread, random);
    frame.kickPower = choice.power;
    frame.kickKind = "cross";
    pressed.add("pass");
    return;
  }

  if (choice.kind === "pass") {
    setAttackTarget(null);
    aimAt(player, choice.target, move);
    scatterDir(move, choice.spread, random);
    frame.kickPower = choice.power;
    frame.kickKind = "pass";
    pressed.add("pass");
    return;
  }

  setAttackTarget(choice.target);
  steerTo(player, choice.target, move);

  // Plantarse (la O con balon): si un rival se le echa encima y lo que toca es
  // conducir, se arma la ventana del cuerpo — si le embiste dentro, rebota el
  // otro. Es la misma O que el cambio de ritmo de abajo, asi que tambien
  // acelera: aguantar el choque y salir es el gesto entero.
  const braceSkill = bodyScale(player);
  if (
    braceSkill > 0 &&
    player.dashCooldown <= 0 &&
    player.burstTime <= 0 &&
    bodyThreat(player, opponents) &&
    random() < AI.BRACE_CHANCE * braceSkill
  ) {
    pressed.add("dash");
    return;
  }

  // Cambio de ritmo: bajo presion, doble toque (E) para adelantar al marcador.
  if (
    pressureDist < AI.FAKE_PRESSURE * 1.15 &&
    player.dashCooldown <= 0 &&
    player.burstTime <= 0 &&
    random() < AI.FAKE_CHANCE * 0.7
  ) {
    pressed.add("dash");
  }
}

/**
 * Portero con balon (tras una parada, un pase atras o recoger un suelto).
 * No es un delantero: no evalua tiros a la porteria rival ni regates bajo
 * presion, solo decide entre sacar corto, despejar largo o asentarse.
 */
function goalkeeperDistribution(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
  frame: AiFrame,
  decide: boolean,
  holdTime: number,
  random: Random,
  setAttackTarget: (target: Vec2 | null) => void,
  urgency: number,
): void {
  const { pressed, move } = frame;

  const pressure = nearestTo(player.pos, opponents);
  const pressureDist = pressure ? dist(pressure.pos, player.pos) : Infinity;
  const underPressure = pressureDist < AI.PRESSURE_RADIUS;

  if (!decide) return;

  // Asentar el balon antes de sacar. Sin esto se sacaba en el mismo tick de
  // la parada, sin dar tiempo a que un companero se abra a recibir. Ganando
  // al final, ademas, se pierde tiempo: sin nadie encima, el portero lo
  // aguanta un rato mas (con presion saca como siempre).
  const settle =
    urgency < 0 && !underPressure
      ? AI.GK_MIN_HOLD - urgency * TACTICS.PROTECT_GK_SETTLE
      : AI.GK_MIN_HOLD;
  if (holdTime < settle) {
    const holdSpot = goalkeeperHoldSpot(player);
    setAttackTarget(holdSpot);
    steerTo(player, holdSpot, move);
    return;
  }

  /*
   * El saque va a un COMPANERO, o no va.
   *
   * Antes eran dos decisiones sueltas: si el companero mas cercano estaba libre
   * se sacaba corto, y si no, se despejaba largo al mas adelantado. Las dos
   * miraban al jugador y ninguna miraba el CAMINO, asi que el saque de despues
   * de un gol —que es cuando el rival esta subiendo y hay cuerpos por medio—
   * salia una y otra vez a los pies de un contrario. Sintoma exacto: "el
   * portero se la pasa a un rival".
   *
   * Ahora se puntua a todos contra el pasillo que hay que atravesar y gana el
   * que se puede alcanzar de verdad. Y si no hay ninguno alcanzable, NO se
   * inventa un pase: se despeja a espacio vacio, que sera un mal saque pero
   * nunca es un regalo.
   */
  const outlets = safeOutlets(player, ball, mates, opponents);

  /*
   * Tranquilo y con un apoyo corto limpio: saque al pie, rodado.
   *
   * El corto tiene preferencia sobre el mejor puntuado aunque el largo progrese
   * mas. Es la unica jerarquia que hay aqui y es deliberada: un despeje siempre
   * "avanza" mas que un pase al central, pero regala el balon la mitad de las
   * veces. Sin presion no hay ninguna prisa por regalarlo.
   */
  const shortOne = outlets.find((o) => o.short) ?? null;
  const outlet = shortOne && !underPressure ? shortOne : (outlets[0] ?? null);

  if (outlet && (!underPressure || outlet.lane >= gkLaneNeed() * 1.6)) {
    setAttackTarget(null);
    aimAt(player, outlet.target, move);
    /*
     * La dispersion se escala con lo limpio que este el pasillo.
     *
     * Un despeje largo lleva error a proposito (un balon al pie exacto es un
     * regalo al defensa que marca), pero ese mismo error, sumado a un pasillo
     * justo, es lo que acababa metiendo el balon en el pie del rival. Con
     * pasillo de sobra se dispersa entero; con pasillo justo, nada.
     */
    if (!outlet.short) {
      const margin = Math.min(1, outlet.lane / (AI.LANE_CLEARANCE * 2.5));
      scatterDir(move, AI.GK_LONG_KICK_SPREAD * margin, random);
    }
    frame.kickPower = outlet.short ? AI.GK_SHORT_KICK_POWER : AI.GK_LONG_KICK_POWER;
    frame.kickKind = "pass";
    pressed.add("pass");
    return;
  }

  /*
   * Nadie alcanzable. Se aguanta el balon un rato —los companeros se estan
   * moviendo, y un segundo mas suele abrir una linea— y solo si el tiempo se
   * acaba o hay presion encima se despeja a la banda contraria, a espacio.
   */
  if (holdTime < AI.GK_MAX_HOLD && !underPressure) {
    const holdSpot = goalkeeperHoldSpot(player);
    setAttackTarget(holdSpot);
    steerTo(player, holdSpot, move);
    return;
  }

  setAttackTarget(null);
  aimAt(player, emptySpaceClearance(player, opponents), move);
  frame.kickPower = AI.GK_LONG_KICK_POWER;
  frame.kickKind = "pass";
  pressed.add("pass");
}

/** Un destino de saque ya evaluado: a donde, por que pasillo y de que tipo. */
interface GkOutlet {
  target: Vec2;
  /** Holgura minima del pasillo hasta el destino. */
  lane: number;
  /** Corto (al pie, rodado) o largo (con recorrido y algo de dispersion). */
  short: boolean;
}

/**
 * Holgura que se le exige al pasillo del saque, con SUELO FISICO.
 *
 * `AI.LANE_CLEARANCE` es un 28 fijo, pero el balon mide 13 de radio en 2v2/3v3
 * y 6 en 11v11. En lados chicos ese 28 deja solo 15 unidades de margen — menos
 * que el diametro del balon— asi que el portero aprobaba pasillos por los que
 * el balon no cabe y sacaba directo al rival. Es el sintoma que se reporto:
 * "en 2v2 y 3v3, al recibir gol, los compañeros la pasan facilisimo al
 * contrario".
 *
 * Es el MISMO fallo que ya se arreglo en `evaluatePass` y con el mismo remedio;
 * al saque de porteria se le habia olvidado aplicar.
 *
 * Y en lados chicos se pide todavia mas: con dos o tres por equipo, perder el
 * balon en el saque es encajar otra vez, no "una perdida mas". No hay bloque
 * detras que lo tape.
 */
function gkLaneNeed(): number {
  const ballR = ACTIVE_MATCH_MODE.physics.ballRadius;
  const small = fieldPlayersPerTeam() <= 3 ? 1.5 : 1;
  return Math.max(ballR * 2 + AI.LANE_MIN_MARGIN, AI.LANE_CLEARANCE) * small;
}

/**
 * Los companeros a los que de verdad se les puede sacar, mejor primero.
 *
 * Se puntua a TODOS los de campo, no solo al mas cercano ni solo al mas
 * adelantado, y lo que manda es el pasillo: de nada sirve que el destinatario
 * este solo si hay un rival plantado en el camino. Se descarta a quien tenga el
 * pasillo por debajo de `LANE_CLEARANCE`; devolver una lista VACIA es una
 * respuesta valida, y es la que evita el pase al rival.
 */
function safeOutlets(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
): GkOutlet[] {
  const dir = attackDir(player.side);
  const found: Array<{ outlet: GkOutlet; score: number }> = [];

  for (const mate of mates) {
    if (mate.role === "gk") continue;
    const d = dist(player.pos, mate.pos);
    const short = d <= AI.SHORT_PASS_MAX;

    /*
     * Al pie si es corto; por delante si es largo.
     *
     * El adelanto del despeje largo NO es cosmetico y por eso entra ANTES de
     * medir el pasillo: si se midiera contra el jugador y el balon fuera a un
     * punto distinto, se estaria aprobando un camino que el balon no recorre.
     */
    const target = short
      ? vec(mate.pos.x, mate.pos.y)
      : vec(
          clamp(mate.pos.x + dir * AI.GK_LONG_CLEAR_LEAD, mate.radius, FIELD.WIDTH - mate.radius),
          mate.pos.y,
        );

    const lane = laneClearance(ball.pos, target, opponents);
    if (lane < gkLaneNeed()) continue;

    const marker = nearestTo(target, opponents);
    const openness = marker ? dist(marker.pos, target) : Infinity;

    // El pasillo pesa mas que el avance: un saque que llega y no progresa sigue
    // siendo posesion, y uno que no llega es del rival.
    const score =
      Math.min(lane, AI.FREE_RADIUS) * 1.4 +
      Math.min(openness, AI.FREE_RADIUS) * 0.9 +
      target.x * dir * 0.25;

    found.push({ outlet: { target, lane, short }, score });
  }

  // Orden estable por puntuacion: el lockstep exige que dos clientes con el
  // mismo estado elijan lo mismo, y `sort` sobre puntuaciones empatadas no lo
  // garantiza si el desempate depende del orden de llegada. Aqui el orden de
  // llegada es el del array de jugadores, que si es identico en los dos.
  found.sort((a, b) => b.score - a.score);
  return found.map((f) => f.outlet);
}

/**
 * Despeje de ultimo recurso: a la banda mas vacia, lejos de todo el mundo.
 *
 * No apunta a nadie a proposito. Es peor saque que cualquier pase, pero un
 * balon suelto en campo abierto se disputa; uno puesto en el pie de un rival ya
 * esta perdido.
 */
function emptySpaceClearance(player: Player, opponents: readonly Player[]): Vec2 {
  const dir = attackDir(player.side);
  const x = clamp(player.pos.x + dir * FIELD.WIDTH * 0.45, 0, FIELD.WIDTH);
  const top = vec(x, FIELD.HEIGHT * 0.16);
  const bottom = vec(x, FIELD.HEIGHT * 0.84);
  const topRoom = opponents.length ? dist(nearestTo(top, opponents)!.pos, top) : Infinity;
  const bottomRoom = opponents.length ? dist(nearestTo(bottom, opponents)!.pos, bottom) : Infinity;
  return topRoom >= bottomRoom ? top : bottom;
}

/**
 * Punto de espera dentro del area propia mientras el portero asienta el
 * balon. Nunca sale del area grande: conducir fuera de ella es regalar un
 * gol en propia puerta, no ganar tiempo.
 */
function goalkeeperHoldSpot(player: Player): Vec2 {
  const dir = attackDir(player.side);
  const boxMinX = player.side === "home" ? 0 : FIELD.WIDTH - FIELD.BOX_WIDTH;
  const boxMaxX = player.side === "home" ? FIELD.BOX_WIDTH : FIELD.WIDTH;

  return vec(
    clamp(player.pos.x + dir * 30, boxMinX + player.radius, boxMaxX - player.radius),
    clamp(
      player.pos.y,
      HALF_HEIGHT - FIELD.BOX_HEIGHT / 2 + player.radius,
      HALF_HEIGHT + FIELD.BOX_HEIGHT / 2 - player.radius,
    ),
  );
}

/**
 * Sin balon: posicionamiento tactico por rol, fase e instrucciones de equipo.
 */
function offBall(
  player: Player,
  ball: Ball,
  owner: Player | null,
  mates: readonly Player[],
  opponents: readonly Player[],
  team: TeamTacticsState,
  setDesired: SetDesired,
): void {
  const plan = planOffBall(player, ball, owner, mates, opponents, team);
  setDesired(plan.x, plan.y, plan.arrive, plan.exact);
}

/**
 * Portero. Se coloca en la linea que une balon y centro de su porteria, a poca
 * distancia de la raya, y sale a por el balon si le entra en el area cercana.
 */
function goalkeeping(
  player: Player,
  ball: Ball,
  owner: Player | null,
  players: readonly Player[],
  humanId: string | null,
  random: Random,
  setDesired: SetDesired,
  reaction: GkReaction,
): void {
  const goal = defendedGoal(player.side);
  const ballDist = dist(player.pos, ball.pos);
  const ballSpeed = len2(ball.vel.x, ball.vel.y);

  // Disparo a porteria: leer cruce, esperar reaccion, luego lanzarse.
  if (ballDist < AI.GK_REACT_DISTANCE && ballSpeed >= GK.THREAT_MIN_SPEED) {
    const crossing = predictGoalLineCrossing(ball, goal.x);
    if (crossing !== null) {
      const read = crossing + signed(random) * AI.GK_PREDICTION_ERROR;
      const mouth = goalMouth();
      const diveY = clamp(read, mouth.top, mouth.bottom);

      if (reaction.diveY === null || Math.abs(diveY - reaction.diveY) > 24) {
        reaction.diveY = diveY;
        reaction.reactionLeft =
          (GK.REACTION_BASE + (ballSpeed / 100) * GK.REACTION_PER_SPEED) * keeperReactionScale(player);
      }
      return;
    }
  }

  reaction.diveY = null;
  reaction.reactionLeft = 0;

  const defenders = players.filter(
    (p) => p.side === player.side && p.role !== "gk" && p.id !== humanId,
  );
  const noOneCloser = !defenders.some((d) => dist(d.pos, ball.pos) < ballDist);
  const threat = !owner || owner.side !== player.side;

  if (threat && ballDist < AI.GK_RUSH_DISTANCE && noOneCloser) {
    setDesired(ball.pos.x, ball.pos.y, 0, true);
    return;
  }

  const dx = ball.pos.x - goal.x;
  const dy = ball.pos.y - goal.y;
  const len = len2(dx, dy) || 1;

  const mouth = goalMouth();
  setDesired(
    goal.x + (dx / len) * AI.GK_LINE_DEPTH,
    clamp(goal.y + (dy / len) * AI.GK_LINE_DEPTH, mouth.top, mouth.bottom),
  );
}

/**
 * ¿El balon viene hacia este jugador con velocidad de pase/tiro? Si es asi,
 * hay que sujetar Shift para recibirlo limpio. `POSSESSION.FIRST_TOUCH_SPEED`
 * es la velocidad relativa a la que el toque se vuelve exigente; se usa el
 * mismo umbral aqui para que la IA solo se moleste en prepararse cuando de
 * verdad hace falta, igual que un humano no tensa el cuerpo para un balon
 * que apenas rueda.
 */
function isBracingForBall(player: Player, ball: Ball): boolean {
  const toPlayerX = player.pos.x - ball.pos.x;
  const toPlayerY = player.pos.y - ball.pos.y;
  const dist = len2(toPlayerX, toPlayerY);
  if (dist > 260 || dist < 1e-6) return false;

  const ballSpeed = len2(ball.vel.x, ball.vel.y);
  if (ballSpeed < POSSESSION.FIRST_TOUCH_SPEED) return false;

  // Componente de la velocidad del balon hacia el jugador (producto escalar
  // normalizado): positivo y grande = viene derecho hacia el.
  const closingSpeed = (ball.vel.x * toPlayerX + ball.vel.y * toPlayerY) / dist;
  return closingSpeed > ballSpeed * 0.5;
}

/** Apuntar: el golpeo sale en la direccion del `move`, asi que basta con orientarlo. */
function aimAt(player: Player, target: Vec2, move: Vec2): void {
  const dx = target.x - player.pos.x;
  const dy = target.y - player.pos.y;
  const len = len2(dx, dy) || 1;
  move.x = dx / len;
  move.y = dy / len;
}

/** Ir hacia un punto frenando al llegar, para no orbitarlo eternamente. */
function steerTo(
  player: Player,
  target: Vec2,
  move: Vec2,
  arrive: number = AI.ARRIVE_RADIUS,
): void {
  const dx = target.x - player.pos.x;
  const dy = target.y - player.pos.y;
  const d = len2(dx, dy);

  // El `d < 1e-6` no es paranoia: al ir a por el balon `arrive` vale 0, asi que
  // un jugador justo encima de su objetivo caia en `0/0` y se llevaba `facing`,
  // posicion y de rebote el balon entero a NaN en un solo tick.
  if (d < 1e-6 || d < arrive) {
    move.x = 0;
    move.y = 0;
    return;
  }

  const throttle = d < AI.SLOW_RADIUS ? d / AI.SLOW_RADIUS : 1;
  move.x = (dx / d) * throttle;
  move.y = (dy / d) * throttle;
}
